/**
 * Party Generator Utility
 * Gera automaticamente grupos (parties) para eventos PVP e BC
 *
 * Regras:
 * - Máximo 6 personagens por grupo
 * - Cada grupo deve ter ao menos 1 buff (Xama, Mecanico, Cavaleiro, Arqueira, Guerreira)
 * - Líder: Prioridade para admin, depois maior nível
 */
import { getClassAbbreviatedName } from "./characterTranslation";

const BUFF_CLASSES = ['XAMA', 'MECANICO', 'CAVALEIRO', 'ARQUEIRA', 'GUERREIRA', 'ATALANTA'];
const MAX_PARTY_SIZE = 6;

/**
 * Verifica se o personagem é uma classe buff
 */
export const isBuffClass = (characterClass) => {
  return BUFF_CLASSES.includes(characterClass.toUpperCase());
};

/**
 * Determina o líder do grupo
 * Prioridade: 1) Admin, 2) Maior nível
 */
export const selectPartyLeader = (participants) => {
  if (!participants || participants.length === 0) return null;

  // Primeiro tenta encontrar um admin
  const admins = participants.filter(p => p.user?.role === 'ADMIN');
  if (admins.length > 0) {
    // Se há admins, pega o de maior nível
    return admins.reduce((leader, current) =>
      current.character.level > leader.character.level ? current : leader
    );
  }

  // Se não há admin, pega o de maior nível
  return participants.reduce((leader, current) =>
    current.character.level > leader.character.level ? current : leader
  );
};

/**
 * Distribui personagens em grupos balanceados
 * Garante ao menos 1 buff por grupo
 */
export const generateParties = (participations) => {
  if (!participations || participations.length === 0) {
    return [];
  }

  // Filtra apenas participações confirmadas ou que participaram
  const confirmedParticipants = participations.filter(p =>
    p.status === 'CONFIRMED' || p.status === 'PARTICIPATED'
  );

  if (confirmedParticipants.length === 0) {
    return [];
  }

  // Separa buffs e não-buffs
  const buffs = confirmedParticipants.filter(p =>
    isBuffClass(p.character?.class)
  );
  const nonBuffs = confirmedParticipants.filter(p =>
    !isBuffClass(p.character?.class)
  );

  // Ordena por nível (maior primeiro) para distribuição equilibrada
  buffs.sort((a, b) => b.character.level - a.character.level);
  nonBuffs.sort((a, b) => b.character.level - a.character.level);

  // Calcula número de grupos necessários
  const totalParticipants = confirmedParticipants.length;
  const numParties = Math.ceil(totalParticipants / MAX_PARTY_SIZE);

  // Inicializa grupos vazios
  const parties = Array.from({ length: numParties }, () => []);

  // Distribui buffs primeiro (1 por grupo, round-robin)
  buffs.forEach((buff, index) => {
    const partyIndex = index % numParties;
    parties[partyIndex].push(buff);
  });

  // Distribui não-buffs de forma equilibrada
  nonBuffs.forEach((nonBuff, index) => {
    // Encontra o grupo com menos membros
    const partyIndex = parties.reduce((minIndex, party, index, arr) =>
        party.length < arr[minIndex].length ? index : minIndex
      , 0);

    // Só adiciona se o grupo não estiver cheio
    if (parties[partyIndex].length < MAX_PARTY_SIZE) {
      parties[partyIndex].push(nonBuff);
    } else {
      // Se todos estão cheios, cria novo grupo
      parties.push([nonBuff]);
    }
  });

  // Remove grupos vazios (caso tenha sobrado)
  const validParties = parties.filter(party => party.length > 0);

  // Para cada grupo, determina o líder e ordena (líder primeiro)
  return validParties.map((party, index) => {
    const leader = selectPartyLeader(party);

    // Ordena: líder primeiro, depois buffs, depois por nível
    const sortedParty = party.sort((a, b) => {
      // Líder sempre primeiro
      if (a.id === leader.id) return -1;
      if (b.id === leader.id) return 1;

      // Buffs antes de não-buffs
      const aIsBuff = isBuffClass(a.character.class);
      const bIsBuff = isBuffClass(b.character.class);
      if (aIsBuff && !bIsBuff) return -1;
      if (!aIsBuff && bIsBuff) return 1;

      // Por nível (maior primeiro)
      return b.character.level - a.character.level;
    });

    return {
      id: index + 1,
      members: sortedParty,
      leader: leader,
      buffCount: party.filter(p => isBuffClass(p.character?.class)).length,
      averageLevel: Math.round(
        party.reduce((sum, p) => sum + p.character.level, 0) / party.length
      )
    };
  });
};

/**
 * Gera texto formatado para copiar (Discord/WhatsApp)
 */
export const formatPartiesForCopy = (parties, eventName) => {
  let text = `🎮 *${eventName}* - Divisão de Grupos\n\n`;

  parties.forEach(party => {
    // Separa membros regulares e buffs compartilhados
    const regularMembers = [];
    const sharedBuffs = [];
    
    party.members.forEach(member => {
      // Verifica se é um buff compartilhado (pode estar em vários grupos)
      if (member.isBuff || member.participant?.isBuff) {
        sharedBuffs.push(member);
      } else {
        regularMembers.push(member);
      }
    });

    // Cabeçalho do grupo
    text += `Grupo ${String(party.id).padStart(2, '0')}: \n\n`;

    // Membros regulares numerados
    regularMembers.forEach((member, index) => {
      const leaderSuffix = index === 0 ? ' (Líder da PT)' : '';
      text += `${String(index + 1).padStart(2, '0')}. ${member.character.name} → ${getClassAbbreviatedName(member.character.class)}${leaderSuffix} \n`;
    });

    text += `\n\n`;

    // Buffs compartilhados
    if (sharedBuffs.length > 0) {
      sharedBuffs.forEach(buff => {
        const className = buff.character.class;
        const pilotInfo = buff.pilot || buff.participant?.pilot ? ` (Piloto: ${buff.pilot || buff.participant?.pilot})` : '';
        text += `Buff ${className.charAt(0).toUpperCase() + className.slice(1).toLowerCase()}: ${buff.character.name}${pilotInfo}\n`;
      });
    }

    text += `\n`;
  });

  return text;
};

/**
 * Gera texto formatado para copiar (Discord/WhatsApp)
 */
export const formatListForCopy = (participations, eventName) => {
  let text = `🎮 *${eventName}* \n\n`;
  let index = 1;

  participations.forEach(participation => {
    let pilot = '';
    if (participation.status !== 'CONFIRMED' && participation.status !== 'PARTICIPATED') {
      return;
    }
    if (participation.pilot) {
      pilot = ` (Piloto: ${participation.pilot})`;
    }

    text += `${index}. ${participation.character.name} → ${getClassAbbreviatedName(participation.character.class)} (Lvl ${participation.character.level})${pilot}\n`;
    index++;
  });

  return text;
};

/**
 * Gera estatísticas sobre os grupos gerados
 */
export const getPartiesStats = (parties) => {
  if (!parties || parties.length === 0) {
    return {
      totalParties: 0,
      totalMembers: 0,
      totalBuffs: 0,
      averagePartySize: 0,
      partiesWithoutBuff: 0
    };
  }

  const totalMembers = parties.reduce((sum, party) => sum + party.members.length, 0);
  const totalBuffs = parties.reduce((sum, party) => sum + party.buffCount, 0);
  const partiesWithoutBuff = parties.filter(party => party.buffCount === 0).length;

  return {
    totalParties: parties.length,
    totalMembers,
    totalBuffs,
    averagePartySize: (totalMembers / parties.length).toFixed(1),
    partiesWithoutBuff
  };
};

export default {
  isBuffClass,
  selectPartyLeader,
  generateParties,
  formatPartiesForCopy,
  formatListForCopy,
  getPartiesStats,
  BUFF_CLASSES,
  MAX_PARTY_SIZE
};
